import React from 'react';
import App from '../../../App';
import { AppServices } from '../../../AppServices';
import CertificationRendererManager from '../../../store/Managers/CertificationRendererManager';
import { RegulationManager } from '../../../store/Managers/RegulationManager';
import { Certification } from '../../../store/Models/Certifications';
import { CertificationType, FileTableEntry } from '../../../store/Models/Documents/DocumentModels';
import * as Models from '../../../store/Models/SharedMaterials/DataModel';
import { LbcRedListStatus, localizeSharedMaterialFmdDisclosureType } from '../../../store/Models/SharedMaterials/DataModel';
import { SoldItem } from '../../../store/Models/SoldItems';
import { IStringLocalizer } from '../../../utilities/IStringLocalizer';
import { ToxnotReactModal, ToxnotReactModalBody, ToxnotReactModalFooter } from '../../core/ToxnotReactModal';
import { TableColumn, TableRow, ToxnotReactTable } from '../../core/ToxnotReactTable';
import { SharedMaterialsReactUtilities } from '../SharedMaterialsReactUtilities';
import { DynamicRegulation } from "../../../store/Models/Compliance/DynamicRegulation";

// ===============================================================================

/**
 * Provides specific attributes to the element responsible for handling the various View Details redirects.
 */
export interface ViewDetailsRenderer {
    renderViewDetailsAttributes: (inventoryId: string, publicInventoryId: string, publicInventoryDataIdId: string, startId: string, startContext: string) => { [attribute: string]: any };
    addToLibraryButton_click: (sharedMaterialId: string) => void;
}

// ===============================================================================

export abstract class SharedMaterialDetailDataSection {
    readonly dataSectionId: string;
    readonly title: string;
    readonly localizer: IStringLocalizer;

    protected constructor(dataSectionId: string, localizedTitle: string, localizer: IStringLocalizer) {
        this.dataSectionId = dataSectionId;
        this.title = localizedTitle;
        this.localizer = localizer;
    }

    isExpanded(collapsedSections: string[]) {
        return !collapsedSections.includes(this.dataSectionId);
    }

    // ===============================================================================

    /**
     * Convenience localization function.
     */
    protected localizeString(s: string): string {
        return this.localizer.localizeString(s);
    }

    // ===============================================================================

    abstract hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean;

    // ===============================================================================

    protected getCertExpirationDateLocalizedString(expirationDate: string | null) {
        return expirationDate === null ? this.localizeString("No Expiration Date") : new Date(expirationDate).toLocaleDateString();
    }

    // ===============================================================================

    protected addToLibraryButton = (renderer: ViewDetailsRenderer, sharedMaterialId: string, inventoryId: string, publicInventoryId: string, publicInventoryDataId: string, startId: string, startContext: string) => {
        renderer.addToLibraryButton_click(sharedMaterialId);
        window.open(`/Data/Inventory?inventoryId=${encodeURIComponent(inventoryId)}&publicInventoryId=${encodeURIComponent(publicInventoryId)}&publicInventoryDataId=${encodeURIComponent(publicInventoryDataId)}&mode=Passport&startControllerId=${encodeURIComponent(startId)}&startControllerContentContext=${encodeURIComponent(startContext)}`, "_blank")
        $('#warn-about-adding-passport-to-library').modal('hide');
    }
    
    // ===============================================================================

    protected closeCatalogWarningModal_click = (e: React.MouseEvent) => {
        e.nativeEvent.stopImmediatePropagation();
        $('#warn-about-adding-passport-to-library').modal('hide');

    }

    // ===============================================================================

    protected renderQuestionMarkIfStringEmpty(s: string) {
        return s.trim().length === 0 ? "?" : s;
    }

    // ===============================================================================

    public static renderDocument(fileId: string, index: number, fileTable: { [fileId: string]: FileTableEntry }, implementationInventoryId: string, trackEventName?: string, trackEventContext?: any, appServices?: AppServices, isGeneralFile: boolean = false) {
        const fileTableEntry = fileTable[fileId];

        if (!fileTableEntry) {
            return null;
        }

        let getFileUrl = `/Api/InventoryFile?inventoryId=${encodeURIComponent(implementationInventoryId)}&fileId=${encodeURIComponent(fileId)}`;

        if (isGeneralFile) {
            getFileUrl = `/Api/v2/GeneralFile?fileId=${encodeURIComponent(fileId)}`;
        }

        if (trackEventName && appServices) {
            return (
                <div key={index} className="file-info-row data-section-row"><a role="button" onClick={e => SharedMaterialDetailDataSection.clickDocumentLink(appServices, getFileUrl, trackEventName, trackEventContext)} download>{fileTableEntry.filename}</a></div>
            );
        } else {
            return (
                <div key={index} className="file-info-row data-section-row"><a role="button" href={getFileUrl} download>{fileTableEntry.filename}</a></div>
            );
        }
    }

    private static clickDocumentLink(appServices: AppServices, link: string, trackEventName: string, trackEventContext?: any) {
        if (appServices) {
            appServices.trackEvent(trackEventName, trackEventContext);
        }
        window.open(link);
    }

    // ===============================================================================
    protected renderViewDetails(coreData: Models.SharedMaterialCoreData, renderer: ViewDetailsRenderer, startContext: string, startId?: string, localizedText?: string) {
        const attributes = renderer.renderViewDetailsAttributes(coreData.implementationInventoryId,
            coreData.publicInventoryId, coreData.publicInventoryDataId, startId ?? "data-pages", startContext);
        const text = localizedText ?? this.localizeString("View details");
        return (
            <div className="view-details"><a {...attributes}>{text}</a>
                <ToxnotReactModal modalId={"warn-about-adding-passport-to-library"} localizedTitle={this.localizeString("Warning")}>
                    <ToxnotReactModalBody>
                        <div className="alert alert-warning">
                            <p>
                                {this.localizeString("To view the details you must add the Passport to your Library.")}
                            </p>
                            <p>
                                {this.localizeString("Do you wish to continue?")}
                            </p>
                        </div>
                    </ToxnotReactModalBody>
                    <ToxnotReactModalFooter localizer={this.localizer} enabledCloseButton={false}>
                        <button className="btn btn-default" onClick={e => this.addToLibraryButton(renderer, coreData.sharedMaterialId, coreData.implementationInventoryId,
                            coreData.publicInventoryId, coreData.publicInventoryDataId, startId ?? "data-pages", startContext)}>{this.localizeString("Continue")}</button>
                        <button className="btn btn-default" onClick={this.closeCatalogWarningModal_click}>{this.localizeString("Go Back")}</button>
                    </ToxnotReactModalFooter>
                </ToxnotReactModal></div>
        );
    }

    abstract render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element;
}

class DetailDataSection_Ingredients extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("Ingredients", localizer.localizeString("Ingredients"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.bomData !== null
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string) {
        return (
            <div className="content">
                <span className="bold">Threshold Level:</span> {localizeSharedMaterialFmdDisclosureType(coreData.fmdStatus!, this.localizer)}
                {this.renderViewDetails(coreData, renderer, "", "bom-editor", this.localizeString("View Ingredients"))}
            </div>
        );
    }
}

class DetailDataSection_Compliance extends SharedMaterialDetailDataSection {
    private readonly _dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>;

    constructor(localizer: IStringLocalizer, dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>) {
        super("Compliance", localizer.localizeString("Compliance"), localizer);

        this._dynamicRegulationTable = dynamicRegulationTable;
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return this.legacyRegsHaveData(coreData, restrictedData) ||
            Object.keys(coreData.regulationIdToComplianceScoreMap).length > 0;
    }

    private legacyRegsHaveData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData) {
        return coreData.scoreCal65 !== null ||
            coreData.scoreReach !== null ||
            coreData.scoreReachAuth !== null ||
            coreData.scoreReachAuthV2 !== null ||
            coreData.scoreRohs !== null ||
            coreData.scoreCm !== null ||
            restrictedData.lbcRedListStatus !== null;
    }

    private renderComplianceRow(startContext: string, complianceScore: Models.StandardComplianceScoreType | null, localizedTitle: string, element: JSX.Element) {
        const scoreElement = SharedMaterialsReactUtilities.getComplianceIconForComplianceDetermination(complianceScore);

        return (
            <div key={startContext} className="compliance-score-row data-section-row">
                <div className="score">{scoreElement}</div>
                <div className="title">{localizedTitle}</div>
                {element}
            </div>
        );
    }

    private renderNoData() {
        return <div className="action no-data">{this.localizeString("No Data")}</div>;
    }

    private renderComplianceScoreRow(coreData: Models.SharedMaterialCoreData, complianceScore: Models.StandardComplianceScoreType | null,
        localizedTitle: string, startContext: string, renderer: ViewDetailsRenderer) {
        return this.renderComplianceRow(startContext, complianceScore, localizedTitle,
            complianceScore !== null
                ? this.renderViewDetails(coreData, renderer, startContext, "compliance-regulation")
                : this.renderNoData()
        );
    }

    private declarationStatusToFriendlyDescriptionMap: { [s: string]: string } = {
        [LbcRedListStatus.Declared]: this.localizeString("Declared"),
        [LbcRedListStatus.RedListApproved]: this.localizeString("LBC Red List Approved"),
        [LbcRedListStatus.RedListFree]: this.localizeString("LBC Red List Free"),
        [LbcRedListStatus.Unknown]: this.localizeString("Unknown")
    };

    private renderLbcRedListStatusRow(restrictedData: Models.SharedMaterialDetailRestrictedData) {
        return this.renderComplianceRow("lbc-red-list", null, this.localizeString("LBC Red List Status"),
            null !== restrictedData.lbcRedListStatus
                ? <div>{this.declarationStatusToFriendlyDescriptionMap[restrictedData.lbcRedListStatus]}</div>
                : this.renderNoData()
        );
    }

    private renderComplianceScoreRows(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData, fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        const complianceScoreRowTuples = Object.keys(coreData.regulationIdToComplianceScoreMap)
            .filter(r => this._dynamicRegulationTable[r])
            .map(regulationId => ({
                regulationId,
                regulationName: this._dynamicRegulationTable[regulationId].config.name,
                jsxElement: this.renderComplianceScoreRow(coreData, coreData.regulationIdToComplianceScoreMap[regulationId], this._dynamicRegulationTable[regulationId].config.name, regulationId, renderer)
            }))
            .concat([
                { regulationId: RegulationManager.CaProp65RegulationId, regulationName: this.localizeString("Cal Prop. 65"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreCal65, this.localizeString("Cal Prop. 65"), RegulationManager.CaProp65RegulationId, renderer) },
                { regulationId: RegulationManager.ReachSvhcCandidateRegulationId, regulationName: this.localizeString("EU REACH SVHC Candidate List"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreReach, this.localizeString("EU REACH SVHC Candidate List"), RegulationManager.ReachSvhcCandidateRegulationId, renderer) },
                { regulationId: RegulationManager.ReachSvhcAuthorizationRegulationId, regulationName: this.localizeString("EU REACH Authorisation"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreReachAuth, this.localizeString("EU REACH Authorisation List"), RegulationManager.ReachSvhcAuthorizationRegulationId, renderer) },
                { regulationId: RegulationManager.ReachSvhcAuthorizationV2RegulationId, regulationName: this.localizeString("EU REACH Authorisation and Restriction"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreReachAuth, this.localizeString("EU REACH Authorisation and Restriction"), RegulationManager.ReachSvhcAuthorizationV2RegulationId, renderer) },
                { regulationId: RegulationManager.RohsRegulationId, regulationName: this.localizeString("RoHS"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreRohs, this.localizeString("RoHS"), RegulationManager.RohsRegulationId, renderer) },
                { regulationId: RegulationManager.ConflictMineralsV2RegulationId, regulationName: this.localizeString("Conflict Minerals (CMRT)"), jsxElement: this.renderComplianceScoreRow(coreData, coreData.scoreCmrtV2, this.localizeString("Conflict Minerals (CMRT)"), RegulationManager.ConflictMineralsV2RegulationId, renderer) },
            ])
            .filter(tuple => tuple.regulationId !== RegulationManager.LbcRedListListId)
            .distinctByStringProjection(tuple => tuple.regulationId);

        if (restrictedData.complianceDetail.hasScipData) {
            complianceScoreRowTuples.push({
                regulationId: "scip",
                regulationName: this.localizeString("SCIP"),
                jsxElement: this.renderComplianceScoreRow(coreData, null, this.localizeString("SCIP"), RegulationManager.ReachSvhcCandidateRegulationId, renderer)
            });
        }

        let cmrtTemplateDetail: JSX.Element | null = null;

        if (coreData.regulationIdToComplianceScoreMap.hasOwnProperty(RegulationManager.ConflictMineralsTemplateRegulationId)) {
            cmrtTemplateDetail = this.renderComplianceRow(RegulationManager.ConflictMineralsRegulationId, null, this.localizeString("Conflict Minerals Template (CMRT) Data"),
                    this.renderViewDetails(coreData, renderer, RegulationManager.ConflictMineralsRegulationId, "compliance-regulation"));
        }

        return (
            <>
                {complianceScoreRowTuples.orderBy(tuple => tuple.regulationName).map(tuple => tuple.jsxElement)}
                {cmrtTemplateDetail}
            </>
        );
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {

        const firstRegulationId = Object.keys(coreData.regulationIdToComplianceScoreMap)[0];

        return (
            <div className="content">
                {this.renderComplianceScoreRows(coreData, restrictedData, fileTable, renderer, companyTeamOwnerId)}
                {this.renderLbcRedListStatusRow(restrictedData)}
                {this.hasData(coreData, restrictedData) && !this.legacyRegsHaveData(coreData, restrictedData) && this.renderViewDetails(coreData, renderer, firstRegulationId, "compliance-regulation")}
            </div>
        );
    }
}

class DetailDataSection_Sustainability extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("Sustainability", localizer.localizeString("Lifecycle Impacts"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.sustainability != null;
    }

    private renderSustainability(localizedTitle: string, localizedAmountLabel: string, data: Models.SharedMaterialSustainabilityData, fileTable: { [fileId: string]: FileTableEntry; }, implementationInventoryId: string) {
        return (
            <div className="group">
                <div className="title">{localizedTitle}</div>
                <div className="content">
                    <div className="data-section-row"><span className="bold">{localizedAmountLabel}:</span> {this.renderQuestionMarkIfStringEmpty(data.amount)}</div>
                    <div className="data-section-row"><span className="bold">{this.localizeString("Product Unit")}:</span> {this.renderQuestionMarkIfStringEmpty(data.productUnit)}</div>
                    <div className="data-section-row"><span className="bold">{this.localizeString("Scope(s)")}:</span> {this.renderQuestionMarkIfStringEmpty(data.scope.map(s => Models.getFriendlyNameFromScope(this.localizer, s)).join(", "))}</div>
                    {data.supportingDocumentFileIds.map((fileId, index) => SharedMaterialDetailDataSection.renderDocument(fileId, index, fileTable, implementationInventoryId))}
                </div>
            </div>
        );
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        const implementationInventoryId = coreData.implementationInventoryId;

        const embodiedCarbonContent: JSX.Element | null = (restrictedData.sustainability!.embodiedCarbon !== null) ?
            this.renderSustainability(this.localizeString("Embodied Carbon"), "KgCO2e", restrictedData.sustainability!.embodiedCarbon, fileTable, implementationInventoryId) :
            null;

        const waterUseContent: JSX.Element | null = (restrictedData.sustainability!.waterUse !== null) ?
            this.renderSustainability(this.localizeString("Water Use"), this.localizeString("Amount of Water Used (liters)"), restrictedData.sustainability!.waterUse, fileTable, implementationInventoryId) :
            null;

        return (
            <div className="content">
                {embodiedCarbonContent}
                {waterUseContent}
                {restrictedData.sustainability!.transparency !== null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Transparency")}</div>
                        <div className="content">
                            {this.renderViewDetails(coreData, renderer, "", "carbon", "View in Lifecycle Details")}
                        </div>
                    </div>}
                {restrictedData.sustainability!.assessmentCommitmentOptimization !== null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Assessment, Commitment, & Optimizations")}</div>
                        <div className="content">
                            {this.renderViewDetails(coreData, renderer, "", "carbon", "View in Lifecycle Details")}
                        </div>
                    </div>}
                {this.renderViewDetails(coreData, renderer, "", "carbon", "View Lifecycle Details")}
            </div>
        );
    }
}

class DetailDataSection_Circularity extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("Circularity", localizer.localizeString("Circularity"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.circularity !== null;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        const circularity = restrictedData.circularity!;

        const hasBomData = restrictedData.bomData !== null;
        const hasRapidlyRenewableContentRollUpPercent = hasBomData && circularity.rapidlyRenewableContentRollUpPercent.length > 0;
        const hasPostConsumerRecyclableContentRollUpPercent = hasBomData && circularity.postConsumerRecyclableContentRollUpPercent.length > 0;
        const hasPreConsumerRecyclableContentRollUpPercent = hasBomData && circularity.preConsumerRecyclableContentRollUpPercent.length > 0;
        const hasRecycledContentRollUpPercent = hasBomData && circularity.recycledContentRollUpPercent.length > 0;
        const hasAnyRenewableAndRecyclableData = hasRapidlyRenewableContentRollUpPercent || hasPostConsumerRecyclableContentRollUpPercent || hasPreConsumerRecyclableContentRollUpPercent || hasRecycledContentRollUpPercent;

        return (
            <div className="content">
                {hasAnyRenewableAndRecyclableData && (
                    <div className="group">
                        <div className="title">{this.localizeString("Renewable Content And Recyclable Content Data")}</div>
                        <div className="content">
                            {hasRapidlyRenewableContentRollUpPercent && <div className="data-section-row"><span className="bold">{this.localizeString("Rapidly Renewable Content")}:</span> {circularity.rapidlyRenewableContentRollUpPercent}</div>}
                            {hasPostConsumerRecyclableContentRollUpPercent && <div className="data-section-row"><span className="bold">{this.localizeString("Post-Consumer Recycled Content")}:</span> {circularity.postConsumerRecyclableContentRollUpPercent}</div>}
                            {hasPreConsumerRecyclableContentRollUpPercent && <div className="data-section-row"><span className="bold">{this.localizeString("Pre-Consumer Recycled Content")}:</span> {circularity.preConsumerRecyclableContentRollUpPercent}</div>}
                            {hasRecycledContentRollUpPercent && <div className="data-section-row"><span className="bold">{this.localizeString("Recycled Content")}:</span> {circularity.recycledContentRollUpPercent}</div>}
                        </div>
                    </div>
                )}
                {circularity.packaging !== Models.SharedMaterialPackaging.Undefined && (
                    <div className="group">
                        <div className="title">{this.localizeString("Packaging")}</div>
                        <div className="content">
                            {circularity.packaging === Models.SharedMaterialPackaging.Included ?
                                this.localizeString("Packaging is included") :
                                this.localizeString("Packaging is not included")}
                        </div>
                        <div className="content">
                            <div className="data-section-row"><span className="bold">{this.localizeString("Packaging Information")}:</span>
                                {(circularity.packagingInformation !== null) && circularity.packagingInformation.split('\n').map((s, index) => (
                                    <p key={index}>
                                        {s.trim()}
                                    </p>
                                ))
                                }
                            </div>
                        </div>
                    </div>
                )}
                <div className="group">
                    <div className="content">
                        {circularity.eolContact.length > 0 && <div className="data-section-row"><span className="bold">{this.localizeString("Contact")}:</span> {circularity.eolContact}</div>}
                    </div>
                </div>
                {circularity.eolOptions.map(option => this.renderEolOption(option, coreData, fileTable))}
                {(circularity.productCircularityDataSheetFileId !== null) &&
                    <div className="group">
                        <div className="title">{this.localizeString("Product Circularity Data Sheet")}</div>
                        <div className="content">
                            <a href={`/Api/GeneralFile?overrideTeamOwnerId=${companyTeamOwnerId}&fileId=${encodeURIComponent(circularity.productCircularityDataSheetFileId)}&attachment=false`} target="_blank">{this.localizeString("View PCDS document")}</a>
                        </div>
                    </div>
                }
                {this.renderViewDetails(coreData, renderer, "standard_page_circularity")}
            </div>
        );
    }

    private renderEolOption(option: Models.SharedMaterialEolOption, coreData: Models.SharedMaterialCoreData, fileTable: { [fileId: string]: FileTableEntry; }) {
        return (
            <div key={option.type} className="group">
                <div className="title">{this.localizeEolOption(option.type)}</div>
                <div className="content">
                    {(option.description.length > 0) && <div className="data-section-row">
                        {option.description}
                    </div>}
                    {(option.supportingLink.length > 0) && <div className="data-section-row">
                        <a href={option.supportingLink}>{option.supportingLink}</a>
                    </div>}
                    {option.attachmentFileIds.map((fileId, index) =>
                        SharedMaterialDetailDataSection.renderDocument(fileId, index, fileTable, coreData.implementationInventoryId)
                    )}
                    {this.resolveEolOptionRenderer(option)}
                </div>
            </div>
        );
    }

    private localizeEolOption(type: Models.SharedMaterialEolOptionType): string {
        switch (type) {
            case Models.SharedMaterialEolOptionType.DesignedForMaintenanceAndRepair: return this.localizeString("Designed for Maintenance and Repair");
            case Models.SharedMaterialEolOptionType.DesignedForDisassembly: return this.localizeString("Designed for Disassembly");
            case Models.SharedMaterialEolOptionType.DesignedForReUse: return this.localizeString("Designed for Re-use");
            case Models.SharedMaterialEolOptionType.TakeBackProgram: return this.localizeString("Take Back Program");
            case Models.SharedMaterialEolOptionType.Recycling: return this.localizeString("Recycling Instructions");
            case Models.SharedMaterialEolOptionType.Other: return this.localizeString("Other");
            default: return "?";
        }
    }

    private resolveEolOptionRenderer(option: Models.SharedMaterialEolOption): JSX.Element {
        switch (option.type) {
            case Models.SharedMaterialEolOptionType.DesignedForMaintenanceAndRepair: return this.renderEolOption_DesignedForMaintenanceAndRepair(option);
            case Models.SharedMaterialEolOptionType.DesignedForDisassembly: return this.renderEolOption_DesignedForDisassembly(option);
            case Models.SharedMaterialEolOptionType.DesignedForReUse: return this.renderEolOption_DesignedForReUse(option);
            case Models.SharedMaterialEolOptionType.TakeBackProgram: return this.renderEolOption_TakeBackProgram(option);
            case Models.SharedMaterialEolOptionType.Recycling: return this.renderEolOption_Recycling(option);
            case Models.SharedMaterialEolOptionType.Other: return this.renderEolOption_Other(option);
        }
    }

    private renderEolOption_DesignedForMaintenanceAndRepair(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_DesignedForMaintenanceAndRepair;
        return (
            <div className="data-section-row">
                <span className="bold">{this.localizeString("Options")}:</span>
                <ul>
                    {option.maintainedByUntrained && <li>{this.localizeString("The product can be maintained & repaired by untrained personnel at the location of the product use")}</li>}
                    {option.maintainedByTrained && <li>{this.localizeString("The product can be maintained & repaired by trained personnel at the location of the product use")}</li>}
                    {option.maintenanceNotRequired && <li>{this.localizeString("The product requires no maintenance or repair if the intended use of the product is followed")}</li>}
                    {option.maintenanceNotPossible && <li>{this.localizeString("Maintenance or repair of the product during its use period is not possible due to the design of the product")}</li>}
                    {option.consumableReplacedByUntrained && (<li>{this.localizeString("Consumables are easily replaced by untrained personnel")}</li>)}
                    {option.sparePartsAvailable && (<li>{this.localizeString("Spare parts are made available by the manufacturer or an authorized representative during the functional use period of the product")}</li>)}
                </ul>
            </div>
        );
    }

    private renderEolOption_DesignedForDisassembly(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_DesignedForDisassembly;
        return (
            <div className="data-section-row"><span className="bold">{this.localizeString("Disassembly Instructions")}:</span>
                {option.instructions.split('\n').map((s, index) => (
                    <p key={index}>
                        {s.trim()}
                    </p>
                ))
                }
            </div>
        );
    }

    private renderEolOption_DesignedForReUse(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_DesignedForReuse;
        var hasOptionGroup1 = option.minimalModification || option.ceMark || option.refurbishment || option.remanufacturing;
        var hasOptionGroup2 = option.dedicatedCollectionSystem || option.emitted || option.industrialCascading ||
            option.industrialComposter || option.homeComposter || option.biodegestion || option.incineration;
        return (
            <React.Fragment>
                <div className="data-section-row">
                    <span className="bold">{this.localizeString("Options")}:</span>
                    <ul>
                        {option.minimalModification && <li>{this.localizeString("The product is designed for re-use as-is or with minimal modification")}</li>}
                        {option.ceMark && <li>{this.localizeString("The product has the CE mark")}</li>}
                        {option.refurbishment && <li>{this.localizeString("The product is designed for refurbishment")}</li>}
                        {option.remanufacturing && <li>{this.localizeString("The product is designed for remanufacturing")}</li>}
                        {(hasOptionGroup1 && hasOptionGroup2) && <p />}
                        {option.dedicatedCollectionSystem && <li>{this.localizeString("The manufacturer/ industry association has a dedicated collection system in place to gather and deliver products for recycling")}</li>}
                        {option.emitted && <li>{this.localizeString("The portion of the product known to be emitted into the environment during use is designed for that purpose")}</li>}
                        {option.industrialCascading && <li>{this.localizeString("The product is designed for industrial cascading in the biosphere")}</li>}
                        {option.industrialComposter && <li>{this.localizeString("The product is designed for composting in an industrial facility")}</li>}
                        {option.homeComposter && <li>{this.localizeString("The product is designed for composting in a home composter")}</li>}
                        {option.biodegestion && <li>{this.localizeString("The product is designed for clean biodigestion")}</li>}
                        {option.incineration && <li>{this.localizeString("The product is designed for clean incineration/pyrolysis")}</li>}
                    </ul>
                </div>
                <div className="data-section-row">
                    <span className="bold">{this.localizeString("What % of the product is designed for recycling at the same level of quality. The remainder of the materials is foreseen by the manufacturer to be recycled at a lower quality than the original content")}:</span>
                    <div>
                        {option.recyclingPercent}
                    </div>
                </div>
                <div className="data-section-row">
                    <span className="bold">{this.localizeString("What % of the product content is anticipated to become leakage during the use phase due to for example wear & tear, oxidation, erosion, etc")}:</span>
                    <div>
                        {option.leakagePercent}
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private renderEolOption_TakeBackProgram(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_TakeBackProgram;
        return (
            <div className="data-section-row">
                <span className="bold">{this.localizeString("More Information")}:</span>
                <div>
                    {option.instructions}
                </div>
            </div>
        );
    }

    private renderEolOption_Recycling(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_Recycling;
        return (
            <div className="data-section-row">
                <span className="bold">{this.localizeString("Recyling Instructions")}:</span>
                <div>
                    {option.instructions}
                </div>
            </div>
        );
    }

    private renderEolOption_Other(rawOption: Models.SharedMaterialEolOption) {
        var option = rawOption as Models.SharedMaterialEolOption_Other
        return (
            <div className="data-section-row">
                <span className="bold">{this.localizeString("Name")}:</span>&nbsp;
                {option.name}
            </div>
        );
    }
}

class DetailDataSection_Documents extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("Documents", localizer.localizeString("Supporting Documents"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return null !== restrictedData.documentFileIds && restrictedData.documentFileIds.length > 0;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        return (
            <div className="content">
                {restrictedData.documentFileIds.map((fileId, index) => (
                    SharedMaterialDetailDataSection.renderDocument(fileId, index, fileTable, coreData.implementationInventoryId)
                ))}
                {this.renderViewDetails(coreData, renderer, "documents")}
            </div>
        );
    }
}

class DetailDataSection_SafetyDataSheet extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("SafetyDataSheet", localizer.localizeString("Safety Data Sheet"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.sdsData.hasSdsFields;
    }

    private renderSdsSectionRow(localizedTitle: string) {
        return (
          
            <div className="title">
                {localizedTitle}
            </div>
           
        );
    }

    private renderSdsSections() {
        return (
            <div className="group">
                {this.renderSdsSectionRow(this.localizeString("Section 1. Product/Material Information"))}
                {this.renderSdsSectionRow(this.localizeString("Section 2. Hazard Identification"))}
                {this.renderSdsSectionRow(this.localizeString("Section 3. Composition/Information On Ingredients"))}
                {this.renderSdsSectionRow(this.localizeString("Section 4. First Aid Measures"))}
                {this.renderSdsSectionRow(this.localizeString("Section 5. Fire-Fighting Measures"))}
                {this.renderSdsSectionRow(this.localizeString("Section 7. Handling and Storage"))}
                {this.renderSdsSectionRow(this.localizeString("Section 8. Exposure Controls/Personal Protection"))}
                {this.renderSdsSectionRow(this.localizeString("Section 9. Physical and Chemical Properties"))}
            </div>
        );
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        const startContext = "standard_page_sds";
        return (
            <div className="content">
                {this.renderSdsSections()}
                {this.renderViewDetails(coreData, renderer, startContext)}
            </div>
        );
    }
}

class DetailDataSection_Reports extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("Reports", localizer.localizeString("Reports"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return null !== restrictedData.reports && restrictedData.reports.length > 0;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        return (
            <div className="content">
                {restrictedData.reports.map((report, index) => (
                    SharedMaterialDetailDataSection.renderDocument(report.fileId, index, fileTable, coreData.implementationInventoryId, undefined, undefined, undefined, true)
                ))}
            </div>
        );
    }
}

class DetailDataSection_SoldItems extends SharedMaterialDetailDataSection {
    private _selectedSoldItemId: string | null;

    constructor(localizer: IStringLocalizer) {
        super("SoldItems", localizer.localizeString("Sold Items"), localizer);
        this._selectedSoldItemId = null;
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return null !== restrictedData.soldItems && restrictedData.soldItems.length > 0;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {
        return (
            <div className="content">
                <div className="sm-detail-section-simple-table">
                    <div className="sm-detail-section-simple-table-row">
                        <div className="bold sm-detail-section-simple-table-col"></div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Brand")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Name")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("SKU")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Certifications")}</div>
                    </div>
                    {restrictedData.soldItems!.orderBy(s => s.data.manufacturerBrand).map((soldItem, index) => (
                        <div className="sm-detail-section-simple-table-row" key={index}>
                            <div className="sm-detail-section-simple-table-col">
                                {soldItem.data.photoFileId && <img src={"/Api/InventoryFile?inventoryId=" + encodeURIComponent(soldItem.inventoryId) + "&fileId=" + encodeURIComponent(soldItem.data.photoFileId) + "&attachment=false"} style={{ maxHeight: "100px", maxWidth: "100px" }} />}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {soldItem.data.manufacturerBrand}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {soldItem.data.name}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {soldItem.data.sku}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {soldItem.certifications.map((c, index2) => <p key={index2}>{CertificationRendererManager.getFriendlyNameForCertificationType(c.type, this.localizer)} - {c.certificationExternalId} - {this.getCertExpirationDateLocalizedString(c.expirationDate)}</p>)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

class DetailDataSection_Certifications extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("PassportCertifications", localizer.localizeString("Certifications"), localizer);
    }

    private getSoldItemCertificates(restrictedData: Models.SharedMaterialDetailRestrictedData) {
        return restrictedData.soldItems?.selectMany(s => s.certifications) ?? [];
    }

    hasCertificationData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return (null !== restrictedData.certifications && (restrictedData.certifications.length > 0))
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return this.hasCertificationData(coreData, restrictedData) || (null !== restrictedData.soldItems && (restrictedData.soldItems.length > 0));
    }

    private getCertificationUrl(url: string) {
        return <a href={(url.startsWith("http://") || url.startsWith("https://")) ? url : "https://" + url}>{url}</a>
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {

        return (
            <div className="content">
                <div className="sm-detail-section-simple-table">
                    <div className="sm-detail-section-simple-table-row">
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Type")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("URL")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Certification ID")}</div>
                        <div className="bold sm-detail-section-simple-table-col">{this.localizeString("Expiration Date")}</div>
                    </div>
                    {this.hasCertificationData(coreData, restrictedData) && restrictedData.certifications!.map((c, index) => (
                        <div className="sm-detail-section-simple-table-row" key={index}>
                            <div className="sm-detail-section-simple-table-col">
                                {CertificationRendererManager.getFriendlyNameForCertificationType(c.type, this.localizer)}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {this.getCertificationUrl(c.metadata.url)}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {c.certificationExternalId}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {c.expirationDate === null ? this.localizer.localizeString("None") : new Date(c.expirationDate).toLocaleDateString()}
                            </div>
                        </div>
                    ))}
                    {this.getSoldItemCertificates(restrictedData).map((c, index) => (
                        <div className="sm-detail-section-simple-table-row" key={index}>
                            <div className="sm-detail-section-simple-table-col">
                                {CertificationRendererManager.getFriendlyNameForCertificationType(c.type, this.localizer)}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {this.getCertificationUrl(c.metadata.url)}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {c.certificationExternalId}
                            </div>
                            <div className="sm-detail-section-simple-table-col">
                                {this.getCertExpirationDateLocalizedString(c.expirationDate)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
}

class DetailDataSection_VocData extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("VocData", localizer.localizeString("VOC"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.vocData !== null;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {

        return (
            <div className="content">
                {restrictedData.vocData!.transparency != null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Transparency")}</div>
                        <div className="content">
                            <div className="data-section-row"><span className="bold">{this.localizeString("VOC Testing")}:</span> {this.renderQuestionMarkIfStringEmpty(restrictedData.vocData!.transparency.testingTypes.map(t => Models.getFriendlyNameForVocTestingType(this.localizer, t)).join(", "))}</div>
                            <div className="data-section-row"><span className="bold">{this.localizeString("VOC Content")}:</span> {this.renderQuestionMarkIfStringEmpty(restrictedData.vocData!.transparency.vocContent)}</div>
                            {this.renderViewDetails(coreData, renderer, "", "voc", "View VOC Details")}
                        </div>
                    </div>}
                {restrictedData.vocData!.assessmentCommitmentOptimization != null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Assessment, Commitment, & Optimizations")}</div>
                        <div className="content">
                            {this.renderViewDetails(coreData, renderer, "", "voc", "View in VOC Details")}
                        </div>
                    </div>}
            </div>
        );
    }
}


class DetailDataSection_InstructionsData extends SharedMaterialDetailDataSection {
    constructor(localizer: IStringLocalizer) {
        super("DetailDataSection_InstructionsData", localizer.localizeString("Instructions"), localizer);
    }

    hasData(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData): boolean {
        return restrictedData.instructions !== null;
    }

    private getModelsFromMatrix = (instructions: Models.SharedMaterialInstructions) => {
        const ret: { name: string, value: string }[] = [];

        // Assumes there are 2 columns.
        for (let i = 0; i < instructions.specifications!.specifications.length; ++i) {
            const name = instructions.specifications!.specifications[i][0];
            const value = instructions.specifications!.specifications[i][1];

            if (name.trim() === "" || value.trim() === "") {
                continue;
            }

            ret.push({ name: name, value: value });
        }

        return ret;
    }

    private getHeaderRow(): TableColumn<{ name: string, value: string }>[] {
        return [
            {
                value: this.localizeString("Name"),
                isSortable: true,
                getSortableValue: (model: { name: string, value: string }) => model.name,

            },
            {
                value: this.localizeString("Value"),
            }
        ]
    }

    private getRowForTable = (model: { name: string, value: string }, rowIndex: number): TableRow => {
        return {
            cols: [
                model.name,
                model.value
            ]
        }
    }

    private getAllDocumentFileIds(restrictedData: Models.SharedMaterialDetailRestrictedData) {
        const ret: string[] = [];

        if (restrictedData.instructions!.installation != null) {
            ret.unshift(...restrictedData.instructions!.installation.documentFileIds);
        }

        if (restrictedData.instructions!.usePhase != null) {
            ret.unshift(...restrictedData.instructions!.usePhase.documentFileIds);
        }

        return ret;
    }

    render(coreData: Models.SharedMaterialCoreData, restrictedData: Models.SharedMaterialDetailRestrictedData,
        fileTable: { [fileId: string]: FileTableEntry; }, renderer: ViewDetailsRenderer, companyTeamOwnerId: string): JSX.Element {

        return (
            <div className="content">
                {restrictedData.instructions!.installation != null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Installation")}</div>
                        <div className="content">
                            <div className="data-section-row"><span className="bold">{this.localizeString("Notes")}</span>: {this.renderQuestionMarkIfStringEmpty(restrictedData.instructions!.installation.notes)}</div>
                        </div>
                    </div>}
                {restrictedData.instructions!.usePhase != null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Use Phase")}</div>
                        <div className="content">
                            <div className="data-section-row"><span className="bold">{this.localizeString("Life Expectancy")}</span>: {this.renderQuestionMarkIfStringEmpty(restrictedData.instructions!.usePhase.lifeExpectancy)}</div>
                            <div className="data-section-row"><span className="bold">{this.localizeString("Warranty Notes")}</span>: {this.renderQuestionMarkIfStringEmpty(restrictedData.instructions!.usePhase.warrantyNotes)}</div>
                        </div>
                    </div>}
                {restrictedData.instructions!.specifications! !== null &&
                    <div className="group">
                        <div className="title">{this.localizeString("Specifications")}</div>
                        <div className="content">
                            <div id="instructions-specifications-table-container">
                                <ToxnotReactTable
                                    localizer={this.localizer}
                                    allRowModels={this.getModelsFromMatrix(restrictedData.instructions!)}
                                    headerRow={this.getHeaderRow()}
                                    getRowForTable={this.getRowForTable}
                                />
                            </div>
                        </div>
                    </div>}
                {this.getAllDocumentFileIds(restrictedData).map((fileId, index) => SharedMaterialDetailDataSection.renderDocument(fileId, index, fileTable, coreData.implementationInventoryId))}
                {this.renderViewDetails(coreData, renderer, "", "instructions")}
            </div>
        );
    }
}

export function defineDetailSections(localizer: IStringLocalizer, dynamicRegulationTable: Readonly<{ [regulationId: string]: DynamicRegulation }>): SharedMaterialDetailDataSection[] {
    return [
        new DetailDataSection_Ingredients(localizer),
        new DetailDataSection_Compliance(localizer, dynamicRegulationTable),
        new DetailDataSection_SafetyDataSheet(localizer),
        new DetailDataSection_Sustainability(localizer),
        new DetailDataSection_Circularity(localizer),
        new DetailDataSection_Documents(localizer),
        new DetailDataSection_Reports(localizer),
        new DetailDataSection_SoldItems(localizer),
        new DetailDataSection_Certifications(localizer),
        new DetailDataSection_VocData(localizer),
        new DetailDataSection_InstructionsData(localizer)
    ];
}
